import React from 'react';
import { Chip, Typography } from '@mui/material';
import { injectIntl } from 'react-intl';
import { selectedChipSx } from '../chipStyles';
import { LABEL_GAP } from '../Inputs/inputStyles';

/**
 * Example prompts for an AI drawer that asks the adviser to type something.
 *
 * "我應該打乜？" is the most common place an adviser stops. Three examples answer
 * it in one glance and, tapped, fill the box — so the first generation costs a
 * tap instead of a decision.
 *
 * They stay on screen after one is tapped, and the tapped one is marked. An
 * example is a choice among three, not a one-shot hint: an adviser who taps
 * 約見面 and then wants 節日問候 should be able to say so, and taking the row
 * away the moment the box fills makes the second choice look impossible.
 *
 * Only the three drawers with a free-text box use this. Every other AI drawer
 * opens on a form or a picker, which already says what it wants, and a chip
 * there would have nothing to fill.
 *
 * `examples` is `[{ labelId, valueId }]`. They differ wherever the chip is a
 * category and the box wants a sentence — 改寫訊息's 「約見面」 fills a whole
 * message for the adviser to edit — and `valueId` may be left out where the
 * label IS the value, as it is for 短片腳本生成器's keywords.
 */
const ExampleChips = props => {
  const { intl, labelId, examples, value, onSelect } = props;

  if (!examples || examples.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: LABEL_GAP }}>
      <Typography variant="caption" color="textSecondary">
        {intl.formatMessage({ id: labelId })}
      </Typography>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: LABEL_GAP }}>
        {examples.map(({ labelId: chipLabelId, valueId }) => {
          const exampleValue = intl.formatMessage({ id: valueId || chipLabelId });
          // Marked only while the box still holds this example untouched. Edit a
          // word of it and nothing is marked, which is honest: what is in the box
          // is no longer the example.
          const selected = !!value && value === exampleValue;

          return (
            <Chip
              key={chipLabelId}
              label={intl.formatMessage({ id: chipLabelId })}
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => onSelect(exampleValue)}
              sx={selected ? selectedChipSx : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export default injectIntl(ExampleChips);
